import { PageContent } from '@/components/shared/page-content'
import { HeaderSection } from '@/components/shared/header-section'
import { Card } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { useState, useEffect } from 'react'
import { toast } from 'sonner'
import { useMutation, useQuery } from '@apollo/client'
import {
  GET_ADMIN_SETTINGS,
  UPDATE_ADMIN_SETTINGS,
} from './adminSettingsQueries'
import { useDemoMode } from '@/context/DemoModeContext'

interface Setting {
  name: string
  title: string
  type: string
  value: string
  shopSettingId: number
  description: string
  __typename: string
}

// Extract options from the description field
const getOptionsFromDescription = (
  description: string
): { value: string; label: string }[] => {
  const optionsMatch = description.match(/Options: ([^.]+)/)
  if (!optionsMatch) return []

  const optionsString = optionsMatch[1]
  return optionsString.split(',').map(option => {
    const value = option.trim()
    let label = value

    // Convert to readable labels
    switch (value) {
      case 'do_nothing':
        label = 'Do Nothing (Keep Original)'
        break
      case 'translate':
        label = 'Auto-Translate'
        break
      case 'make_all_same':
        label = 'Use Same Text for All Languages'
        break
    }

    return { value, label }
  })
}

export default function AdminSettings() {
  const { data, loading, refetch } = useQuery(GET_ADMIN_SETTINGS)
  const [updateShopSettings] = useMutation(UPDATE_ADMIN_SETTINGS)
  const { isDemoMode, toggleDemoMode } = useDemoMode()

  const [settings, setSettings] = useState<Setting[]>([])
  const [savingSettings, setSavingSettings] = useState<Set<string>>(new Set())

  // Initialize settings when data is loaded
  useEffect(() => {
    if (data?.findManySetting) {
      const settingsData = data.findManySetting as Setting[]
      setSettings(settingsData)
    }
  }, [data])

  const handleSettingChange = async (settingName: string, newValue: string) => {
    // Update local state immediately for responsive UI
    setSettings(prevSettings =>
      prevSettings.map(setting =>
        setting.name === settingName ? { ...setting, value: newValue } : setting
      )
    )

    // Add to saving state
    setSavingSettings(prev => new Set(prev).add(settingName))

    try {
      // Create the update data structure for this specific setting
      const updateData = {
        settings: {
          update: [
            {
              data: {
                value: {
                  set: newValue,
                },
              },
              where: {
                name: settingName,
              },
            },
          ],
        },
      }

      // Use the shopSettingId from the settings
      const shopSettingId =
        settings.find(s => s.name === settingName)?.shopSettingId || 1

      await updateShopSettings({
        variables: {
          data: updateData,
          where: {
            id: shopSettingId,
          },
        },
      })

      toast.success(
        `"${
          settings.find(s => s.name === settingName)?.title
        }" updated successfully`
      )
    } catch (error) {
      console.error(`Failed to update setting ${settingName}:`, error)
      toast.error(
        `Failed to update "${
          settings.find(s => s.name === settingName)?.title
        }"`
      )

      // Revert the local state on error
      const originalValue = data?.findManySetting?.find(
        (s: Setting) => s.name === settingName
      )?.value
      setSettings(prevSettings =>
        prevSettings.map(setting =>
          setting.name === settingName
            ? { ...setting, value: originalValue || setting.value }
            : setting
        )
      )
    } finally {
      // Remove from saving state
      setSavingSettings(prev => {
        const newSet = new Set(prev)
        newSet.delete(settingName)
        return newSet
      })
    }
  }

  if (loading) {
    return (
      <PageContent>
        <div className="flex justify-center items-center h-64">
          <div>Loading...</div>
        </div>
      </PageContent>
    )
  }

  return (
    <PageContent>
      <div className="flex justify-between items-center mb-8">
        <HeaderSection
          title="Admin Settings"
          subtitle="Configure admin-level features and translation settings for products."
        />
      </div>

      <div className="max-w-[1000px]">
        <Card className="p-6">
          <div className="space-y-6">
            {/* Demo Mode Toggle */}
            <div className="flex items-start justify-between py-4 border-b border-gray-100">
              <div className="flex-1">
                <h3 className="text-base font-semibold mb-1">Demo Mode</h3>
                <p className="text-sm text-gray-600 mb-2">
                  Hide sensitive data (company names, account codes)
                </p>
              </div>
              <div className="ml-6">
                <Switch checked={isDemoMode} onCheckedChange={toggleDemoMode} />
              </div>
            </div>

            {settings.map(setting => {
              const options = getOptionsFromDescription(setting.description)
              const isSaving = savingSettings.has(setting.name)

              return (
                <div
                  key={setting.name}
                  className="flex items-start justify-between py-4 border-b border-gray-100 last:border-b-0"
                >
                  <div className="flex-1">
                    <h3 className="text-base font-semibold mb-1">
                      {setting.title}
                    </h3>
                    {setting.description && (
                      <p className="text-sm text-gray-600 mb-2">
                        {setting.description.split('. Options:')[0]}
                      </p>
                    )}
                    <div className="text-xs text-gray-500">{setting.name}</div>
                  </div>

                  <div className="ml-6 min-w-[200px]">
                    <Select
                      value={setting.value}
                      onValueChange={value =>
                        handleSettingChange(setting.name, value)
                      }
                      disabled={isSaving}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {options.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {isSaving && (
                      <div className="text-xs text-gray-500 mt-1">
                        Saving...
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </Card>

        {settings.length === 0 && (
          <Card className="p-6">
            <div className="text-center text-gray-500">
              No admin settings found.
            </div>
          </Card>
        )}
      </div>
    </PageContent>
  )
}
